import { DataType } from '@/components/select-franchise-modal/type';
import {
	AgriFranchiseEnum,
	AllocationMethodEnum,
	AssignmentFranchiseTypeEnum,
	AssignmentSettingUsedEnum,
	MasterInfoEnum,
	OPTION_SELECT,
	RangeAgeEnum,
	TextMessage,
} from '@/constant';
import { useCommonList } from '@/hooks';
import { IProgram } from '@/services';
import { TCommonApplicantParams } from '@/stores';
import { formatDate, insertObjectIfV2, showAlert } from '@/utils';
import { useCallback, useMemo } from 'react';

import { RadioChangeEvent } from 'antd';
import { DefaultOptionType } from 'antd/es/select';

import { Dayjs } from 'dayjs';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { TValues } from '../../define-assignment.config';
import { AssignmentInfoProps } from '.';

export const assignmentInfoLogic = (props: AssignmentInfoProps) => {
	const {
		formik,
		fetchData,
		params,
		rowSelection,
		programCommonList,
		programList,
		applicantPublicMainFranchiseId,
	} = props;
	const { values, validateForm, handleSubmit } = formik;

	const {
		selectMasterInfo: { data: districtList },
	} = useCommonList(MasterInfoEnum.DISTRICT);

	const programOptions = useMemo(() => {
		return [OPTION_SELECT, ...(programList || [])];
	}, [programList]);

	const districtSelected = useMemo(() => {
		return districtList?.find((d) => d.value === values.districtId);
	}, [districtList, values.districtId]);

	const getCommonRequestParams = useCallback(
		(values: any) => {
			return {
				...params,
				franchiseId: values.franchiseId,
				type: values.type,
				publicFranchiseId: values.publicFranchiseId,
				programId: values.programId,
				startDate: values.startDate,
				endDate: values.endDate,
				requestApplicant: values.requestApplicant,
				page: 1,
				assignmentMethod: values.assignmentMethod,
			};
		},
		[params]
	);

	const updateAssignmentMethod = useCallback(
		(method: string) => {
			formik.setValues((prev) => ({
				...prev,
				assignmentMethod: method as AllocationMethodEnum,
				...insertObjectIfV2(method === AllocationMethodEnum.NO_ASSIGNMENT, {
					fromApplicant: false,
					isPublicApplicant: false,
				}),
				assignSetting: {
					...prev.assignSetting,
					...insertObjectIfV2(
						method === AllocationMethodEnum.AUTOMATIC_ASSIGNMENT &&
							Number(prev.assignSetting?.maleRequest) === 0,
						{
							maleRequest: '0',
						}
					),
					...insertObjectIfV2(
						method === AllocationMethodEnum.AUTOMATIC_ASSIGNMENT &&
							Number(prev.assignSetting?.femaleRequest) === 0,
						{
							femaleRequest: '0',
						}
					),
				},
			}));
		},
		[formik]
	);

	const validateAutoAssignment = useCallback(async () => {
		const errors = await validateForm(values);
		if (isNotNilOrEmpty(errors)) {
			handleSubmit();
			showAlert(TextMessage.ALERT_12);
			return false;
		}
		return true;
	}, [validateForm, values, handleSubmit]);

	const validateAssignmentSettings = useCallback(() => {
		if (
			!values.assignSetting ||
			values.isUsed !== AssignmentSettingUsedEnum.USED
		) {
			return true;
		}

		const totalSettingRequest =
			Number(values.assignSetting.maleRequest || 0) +
			Number(values.assignSetting.femaleRequest || 0);

		return totalSettingRequest === Number(values.requestApplicant);
	}, [values]);

	const handleAutoOrManualAssignment = useCallback(
		async (method: string) => {
			const isValid = await validateAutoAssignment();
			if (!isValid) return;

			const isValidSettings =
				method === AllocationMethodEnum.AUTOMATIC_ASSIGNMENT
					? validateAssignmentSettings()
					: true;
			if (!isValidSettings) {
				showAlert(TextMessage.ALERT_70, () => {
					fetchData?.(
						getCommonRequestParams({ ...values, assignmentMethod: method })
					);
					updateAssignmentMethod(method);
				});
				return;
			}

			fetchData?.(
				getCommonRequestParams({ ...values, assignmentMethod: method })
			);
			updateAssignmentMethod(method);
		},
		[
			validateAutoAssignment,
			validateAssignmentSettings,
			fetchData,
			getCommonRequestParams,
			updateAssignmentMethod,
			values,
		]
	);

	const onChangeRadio = useCallback(
		async (e: RadioChangeEvent) => {
			const newMethod = e.target.value;
			const isAutoOrManualAssignment = [
				AllocationMethodEnum.AUTOMATIC_ASSIGNMENT,
				AllocationMethodEnum.MANUAL_ASSIGNMENT,
			].includes(newMethod);

			if (isAutoOrManualAssignment) {
				await handleAutoOrManualAssignment(newMethod);
			} else {
				rowSelection?.onReset();
				updateAssignmentMethod(newMethod);
			}
		},
		[handleAutoOrManualAssignment, rowSelection, updateAssignmentMethod]
	);

	const updateProgramId = useCallback(
		(
			value: string,
			{ startDate, endDate }: { startDate?: string; endDate?: string } = {}
		) => {
			const isCommonType = values.type === AssignmentFranchiseTypeEnum.COMMON;
			const updatedValues = {
				programId: value,
				...insertObjectIfV2(startDate && isCommonType, { startDate }),
				...insertObjectIfV2(endDate && isCommonType, { endDate }),
			};

			formik.setValues((prev) => ({
				...prev,
				...updatedValues,
			}));

			fetchData?.({
				...updatedValues,
				page: 1,
			});
		},
		[fetchData, values.type]
	);

	const handleDateChange = useCallback(
		(fieldName: keyof TValues) =>
			(date: Dayjs, dateString: string | string[]) => {
				const value = formatDate(date, { outputFormat: 'YYYY-MM-DD' });
				formik.setFieldValue(fieldName, value);
				fetchData?.({ [fieldName]: value, page: 1 });
			},
		[formik, fetchData]
	);

	const onChangePublicType = useCallback(
		(e: RadioChangeEvent) => {
			formik.setValues((prev) => ({
				...prev,
				type: e.target.value,
				franchiseId: '',
				franchiseName: '',
				districtId: '',
				publicFranchiseId: '',
			}));

			fetchData?.({
				type: e.target.value,
				publicFranchiseId: '',
				franchiseId: '',
				page: 1,
			});
		},
		[formik, fetchData]
	);

	const getAdditionalValues = useCallback(
		(fieldName: string) => (value: string) => {
			const params: Partial<TCommonApplicantParams> = {
				[fieldName]: value,
			};

			if (fieldName === 'publicFranchiseId') {
				params.franchiseId = '';
			}

			fetchData?.(params);

			return fieldName === 'publicFranchiseId'
				? {
						franchiseId: '',
						franchiseName: '',
						districtId: '',
						assignmentMethod: AllocationMethodEnum.NO_ASSIGNMENT,
						page: 1,
					}
				: {};
		},
		[fetchData]
	);

	const getNestedAdditionalValues = useCallback(
		(fieldName: string) => (value: string, prevValues: any) => {
			if (fieldName.includes('.')) {
				const keys = fieldName.split('.');
				const lastKey = keys.pop()!;
				const firstKey = keys[0];

				const params: Partial<TCommonApplicantParams> = {
					[firstKey]: {
						...prevValues[firstKey],
						[lastKey]: value,
					},
				};

				fetchData?.(params);
				rowSelection?.onReset();
			}
			return insertObjectIfV2(
				prevValues.assignmentMethod ===
					AllocationMethodEnum.AUTOMATIC_ASSIGNMENT,
				{
					assignmentMethod: AllocationMethodEnum.NO_ASSIGNMENT,
				}
			);
		},
		[fetchData]
	);

	const onChangeProgram = useCallback(
		(value: string, option: DefaultOptionType) => {
			const { startDate, endDate } = formik.values;

			if (startDate || endDate) {
				updateProgramId(value);
				return;
			}

			const optionSelected = programCommonList?.data?.find(
				(item) => Number((item as IProgram).id) === Number(value)
			) as IProgram | undefined;
			updateProgramId(value, {
				startDate: optionSelected?.startDate,
				endDate: optionSelected?.endDate,
			});
		},
		[formik.values, programCommonList]
	);

	const onSaveFranchise = useCallback(
		(keys: number[], franchises: DataType) => {
			const franchiseSelected = franchises?.[0];

			if (values.fromApplicant) {
				const isPublicFranchise =
					franchiseSelected?.typeDetail === AgriFranchiseEnum.PUBLIC_FRANCHISE;

				if (
					(values.isPublicApplicant &&
						Number(franchiseSelected?.id) !==
							Number(applicantPublicMainFranchiseId)) ||
					(!values.isPublicApplicant && isPublicFranchise)
				) {
					showAlert(TextMessage.ALERT_55);
					return;
				}
			}

			formik.setValues((prev) => ({
				...prev,
				franchiseId: franchiseSelected?.id,
				franchiseName: franchiseSelected?.name,

				districtId: franchiseSelected?.districtId,
			}));

			fetchData?.({ franchiseId: franchiseSelected?.id, page: 1 });
		},
		[fetchData, values, applicantPublicMainFranchiseId]
	);

	const onResetFranchise = useCallback(() => {
		formik.setValues((prev) => ({
			...prev,
			franchiseId: '',
			franchiseName: '',
			districtId: '',
			assignmentMethod: AllocationMethodEnum.NO_ASSIGNMENT,
		}));
		fetchData?.({ franchiseId: '' });
	}, [fetchData]);

	const onChangeAssignmentSetting = useCallback((e: RadioChangeEvent) => {
		formik.setValues((prev) => ({
			...prev,
			isUsed: e.target.value,

			...insertObjectIfV2(
				e.target.value === AssignmentSettingUsedEnum.NOT_USED,
				{
					assignSetting: {
						maleRequest: '0',
						maleAgeRange: '' as RangeAgeEnum,
						femaleRequest: '0',
						femaleAgeRange: '' as RangeAgeEnum,
					},
				}
			),
		}));
		fetchData?.({ assignSetting: undefined });
	}, []);

	return {
		programOptions,
		onChangeRadio,
		handleDateChange,
		onChangePublicType,
		onChangeAssignmentSetting,
		getAdditionalValues,
		onChangeProgram,
		onSaveFranchise,
		onResetFranchise,
		getNestedAdditionalValues,
		districtSelected,
	};
};
